<template>
    <FullModal ref="modal" title="Event Wizard">
        <WizardSteps ref="wizard" v-model="currentStep" :steps="availableSteps" class="tw-px-6 tw-mt-1" />

        <Transition
            mode="out-in"
            enter-active-class="tw-transition-all tw-duration-200 tw-ease-out motion-reduce:tw-transition-none"
            enter-from-class="tw-translate-x-8 tw-opacity-0"
            enter-to-class="tw-translate-x-0 tw-opacity-100"
            leave-active-class="tw-transition-all tw-duration-200 tw-ease-in motion-reduce:tw-transition-none"
            leave-from-class="tw-translate-x-0 tw-opacity-100"
            leave-to-class="-tw-translate-x-8 tw-opacity-0"
        >
            <div v-if="currentStep === 'Choose Method'" key="choose-method" class="tw-px-6">
                <span class="tw-block tw-text-lg tw-font-semibold tw-mt-4 tw-mb-3">
                    How would you like to create your event?
                </span>
                <div class="tw-grid tw-grid-cols-2 tw-gap-3 tw-mb-6">
                    <RadioBox
                        :checked="createMethod === 'quick'"
                        class="tw-flex tw-flex-col"
                        @click="selectCreateMethod('quick')"
                    >
                        <i class="mdi mdi-lightning-bolt tw-text-eventwise tw-text-3xl tw-mb-2"></i>
                        <span class="tw-font-medium tw-text-base">Quick Create</span>
                        <span class="tw-text-gray-400">Start with essential details.</span>
                    </RadioBox>
                    <RadioBox
                        v-if="
                            Auth.can('manage departments') &&
                            Auth.can('manage subcategories') &&
                            Auth.can('create budget entries') &&
                            Auth.can('manage tickets')
                        "
                        :checked="createMethod === 'template'"
                        class="tw-flex tw-flex-col"
                        @click="selectCreateMethod('template')"
                    >
                        <i class="mdi mdi-view-dashboard-outline tw-text-eventwise tw-text-3xl tw-mb-2"></i>
                        <span class="tw-font-medium tw-text-base">Use a Template</span>
                        <span class="tw-text-gray-400">Reuse a proven setup.</span>
                    </RadioBox>
                    <RadioBox
                        :checked="createMethod === 'copy'"
                        class="tw-flex tw-flex-col"
                        @click="selectCreateMethod('copy')"
                    >
                        <i class="mdi mdi-content-copy tw-text-eventwise tw-text-3xl tw-mb-2"></i>
                        <span class="tw-font-medium tw-text-base">Copy a Previous Event</span>
                        <span class="tw-text-gray-400">Recommended: reuse safe structure.</span>
                    </RadioBox>
                </div>

                <!-- <RadioBox :checked="createMethod === 'ai'"
                    class="tw-w-full tw-flex tw-gap-4 tw-bg-emerald-50 tw-border-2 tw-border-emerald-600/40 hover:tw-border-emerald-600/80 hover:!tw-bg-emerald-50"
                    checked-class="!tw-border-emerald-600 !tw-bg-emerald-50 !tw-ring-emerald-50"
                    @click="createMethod = 'ai'">
                    <i class="mdi mdi-creation-outline tw-text-emerald-600 tw-text-3xl tw-mb-2"></i>
                    <div class="tw-flex tw-flex-col">
                        <span class="tw-font-medium tw-text-base">Create with Eventwise Intelligence</span>
                        <span class="tw-text-gray-400">Describe your event, review ticket economics and scenarios, then
                            confirm a complete plan.</span>
                    </div>
                </RadioBox> -->
            </div>
            <div v-else-if="currentStep === 'Select Event'" key="select-event" class="tw-px-6">
                <span class="tw-block tw-text-lg tw-font-semibold tw-mt-4 tw-mb-3">
                    Let&apos;s find your existing event
                </span>
                <Banner v-if="eventPrefillError" type="is-danger" icon="mdi-alert" class="tw-mt-3">
                    {{ eventPrefillError }}
                </Banner>
                <Textbox
                    v-model="existingEventSearch"
                    placeholder="Search for an event"
                    @update:model-value="getAccountEvents()"
                />
                <div class="tw-overflow-x-auto tw-grid tw-grid-cols-4 tw-gap-3 tw-my-4">
                    <RadioBox
                        v-for="event in accountEvents"
                        :key="event.id"
                        class="tw-w-full tw-flex tw-flex-col"
                        @click="prefillFromPreviousEvent(event)"
                    >
                        <span class="tw-font-medium tw-text-base">{{ event.name }}</span>
                        <span v-if="event.primary_address" class="tw-text-gray-600">
                            {{ event.primary_address.address1 }}, {{ event.primary_address.city }}
                        </span>
                        <span class="tw-text-gray-400 tw-mt-2">
                            {{ localTimestamp(event.starts_at) }}
                        </span>
                    </RadioBox>
                </div>
                <WizardPanel
                    v-if="recentlyEndedEvents.length"
                    title="Recently Ended Events"
                    content-class="tw-pt-3 tw-grid tw-grid-cols-3"
                >
                    <RadioBox
                        v-for="event in recentlyEndedEvents"
                        :key="event.id"
                        class="tw-w-full tw-flex tw-flex-col"
                        @click="prefillFromPreviousEvent(event)"
                    >
                        <span class="tw-font-medium tw-text-sm">{{ event.name }}</span>
                        <span class="tw-text-gray-400 tw-mt-1">Ended {{ localTimestamp(event.ends_at) }}</span>
                    </RadioBox>
                </WizardPanel>
            </div>
            <div v-else-if="currentStep === 'Details'" key="details" class="tw-px-6 tw-pb-5">
                <span class="tw-block tw-text-lg tw-font-semibold tw-mt-4">Let&apos;s get the details right</span>
                <FormField class="tw-mt-2" label="Event or project name">
                    <Textbox v-model="form.name" />
                </FormField>
                <WizardPanel class="tw-my-5" title="Essential Details">
                    <div class="tw-grid tw-grid-cols-2 tw-gap-3 tw-mt-4">
                        <FormField label="Venue">
                            <Autocomplete
                                ref="venueAutocomplete"
                                v-model="form.primary_address_id"
                                :endpoint="
                                    route('api.account.addresses', {
                                        account: accountId,
                                        paginated: 1,
                                    })
                                "
                                placeholder="Search for a venue"
                                :allow-create="true"
                                @selection-change="handleVenueSelection"
                                @create="openCreateVenueModal"
                            >
                                <template #option="{ option }">
                                    <div class="tw-flex tw-flex-col tw-max-w-[400px]">
                                        {{ option.name }}
                                        <span
                                            class="tw-text-gray-400 tw-text-xs tw-overflow-ellipsis tw-overflow-hidden tw-whitespace-nowrap"
                                        >
                                            {{ option.address }}
                                        </span>
                                    </div>
                                </template>
                            </Autocomplete>
                        </FormField>
                        <FormField label="Project Start">
                            <VueFlatPickr
                                v-model="form.project_start"
                                placeholder="Required"
                                :config="{
                                    minDate: form.project_start,
                                    ...flatpickrConfig,
                                }"
                            />
                        </FormField>
                        <FormField label="Event Date(s)">
                            <VueFlatPickr
                                :model-value="form.event_dates"
                                placeholder="Event Date (required)"
                                :config="flatpickrMultiConfig"
                                class="input control is-clearfix"
                                @update:model-value="setMainEventDates"
                                @on-close="flatpickrRangeAllowSingle"
                            />
                        </FormField>
                        <CurrencySelect v-model="form.currency_code" label="Primary Currency" class="tw-col-span-1" />
                    </div>
                </WizardPanel>

                <div class="tw-grid tw-grid-cols-2 tw-items-start tw-gap-3 tw-my-5">
                    <WizardPanel
                        title="Tags"
                        subtext="Choose from existing event tags. Metadata tags are added automatically."
                        class="tw-flex tw-flex-col"
                    >
                        <TagSelector v-model="form.tags" class="tw-mt-2">
                            <template #trigger="{ selectedTags, open }">
                                <div class="tw-flex tw-flex-wrap tw-gap-2 tw-items-center">
                                    <GroupTag
                                        v-for="tag in selectedTags"
                                        :key="tag.id"
                                        :tag="tag"
                                        class="!tw-py-2 !tw-px-3 !tw-text-sm"
                                    />
                                    <Button
                                        class="tw-border-dashed !tw-text-gray-400 tw-rounded-[50px]"
                                        :class="{ '!tw-text-white': open }"
                                        :active="open"
                                    >
                                        <i class="mdi mdi-plus"></i>
                                        Add tags
                                    </Button>
                                </div>
                            </template>
                        </TagSelector>
                    </WizardPanel>
                    <WizardPanel
                        title="Team Members"
                        subtext="Choose which team members should have access to this event."
                        class="tw-flex tw-flex-col"
                    >
                        <TeamSelector v-model="form.users" class="tw-mt-2">
                            <template #trigger="{ selectedUsers, open }">
                                <div class="tw-flex tw-flex-wrap tw-items-center tw-gap-2">
                                    <TeamMemberUser
                                        v-for="user in selectedUsers"
                                        :key="user.id"
                                        :user="user"
                                        :hide-name="false"
                                        size="xsmall"
                                        class="tw-rounded-full tw-border tw-px-2 tw-py-1"
                                    />
                                    <Button
                                        class="tw-border-dashed !tw-text-gray-400 tw-rounded-[50px]"
                                        :class="{ '!tw-text-white': open }"
                                        :active="open"
                                    >
                                        <i class="mdi mdi-plus"></i>
                                        Add team members
                                    </Button>
                                </div>
                            </template>
                        </TeamSelector>
                    </WizardPanel>
                </div>

                <Checkbox v-model="form.is_recurring" class="!tw-mb-0 tw-mt-5">
                    <div class="tw-flex tw-flex-col tw-ml-2">
                        <span class="tw-font-medium tw-text-sm">Event Series</span>
                        <span class="tw-text-gray-400 tw-text-xs">This event is part of a recurring series.</span>
                    </div>
                </Checkbox>
                <Transition
                    appear
                    enter-active-class="tw-transition-all tw-duration-200 tw-ease-out motion-reduce:tw-transition-none"
                    enter-from-class="-tw-translate-y-3 tw-opacity-0"
                    enter-to-class="tw-translate-y-0 tw-opacity-100"
                    leave-active-class="tw-transition-all tw-duration-150 tw-ease-in motion-reduce:tw-transition-none"
                    leave-from-class="tw-translate-y-0 tw-opacity-100"
                    leave-to-class="-tw-translate-y-2 tw-opacity-0"
                >
                    <WizardPanel v-if="form.is_recurring" class="tw-mt-2 tw-flex tw-flex-col !tw-p-0 !tw-pt-4">
                        <div class="tw-flex tw-items-center tw-gap-1 tw-ml-5">
                            Create a series of
                            <NumberInput
                                :model-value="form.recurring_options.length"
                                :min="0"
                                :max="9"
                                class="tw-w-20 tw-ml-2"
                                @update:model-value="resizeRecurringOptions"
                            />
                            additional events
                        </div>
                        <table class="table tw-mt-4">
                            <thead>
                                <tr>
                                    <th>Name</th>
                                    <th>Project Start</th>
                                    <th>Event Dates</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr v-for="(recurringOption, index) in form.recurring_options" :key="index">
                                    <td>
                                        <Textbox v-model="recurringOption.name" placeholder="Event name" />
                                    </td>
                                    <td>
                                        <VueFlatPickr
                                            v-model="recurringOption.project_start"
                                            placeholder="Project Start"
                                            :config="{
                                                minDate: recurringOption.project_start,
                                                ...flatpickrConfig,
                                            }"
                                        />
                                    </td>
                                    <td>
                                        <VueFlatPickr
                                            :model-value="recurringOption.event_dates"
                                            placeholder="Event Date(s)"
                                            :config="{
                                                minDate: recurringOption.project_start,
                                                ...flatpickrMultiConfig,
                                            }"
                                            @update:model-value="setRecurringEventDates(index, $event)"
                                            @on-close="flatpickrRangeAllowSingle"
                                        />
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </WizardPanel>
                </Transition>
            </div>
            <div v-else-if="currentStep === 'Select Template'" key="select-template" class="tw-px-6 tw-pb-5">
                <span class="tw-block tw-text-lg tw-font-semibold tw-mt-4">Select a Template to Import</span>
                <div class="tw-grid tw-grid-cols-2 tw-gap-4 tw-mt-4">
                    <WizardPanel title="Select File">
                        If you have already completed a template file, you can select or drop it here to upload and
                        validate it.
                        <Upload v-model="templateFile" class="tw-mt-4" :accept="['.xlsx']" />
                    </WizardPanel>
                    <WizardPanel title="Download Template" content-class="tw-h-full tw-flex tw-flex-col tw-pb-5">
                        If you haven&apos;t used a template file before, you can download a blank file below.
                        <br />
                        This can then be imported into the panel on the left to set up your event.
                        <Button class="tw-mt-auto" @click="downloadTemplate">
                            <i class="mdi mdi-download tw-mr-1"></i>
                            Download Template File
                        </Button>
                    </WizardPanel>
                </div>
                <WizardPanel title="Template Validation" class="tw-mt-4">
                    <Banner v-if="templateValidationLoading" type="is-info" icon="mdi-loading" class="tw-my-3">
                        Validating your template...
                    </Banner>
                    <template v-else-if="templateFile">
                        <Banner v-if="templateValidation?.isValid" type="is-success" icon="mdi-check" class="tw-my-3">
                            Your template has been validated successfully and is ready to import.
                        </Banner>
                        <Banner
                            v-else-if="templateValidationError || templateValidation"
                            type="is-warning"
                            icon="mdi-alert"
                            class="tw-my-3"
                        >
                            There were some issues validating your template.
                            <p v-if="templateValidationError" class="tw-mb-0 tw-mt-1">{{ templateValidationError }}</p>
                        </Banner>
                        <p class="tw-mt-3 tw-font-medium">{{ templateFile.name }}</p>
                        <template v-if="templateValidation">
                            <p class="tw-mt-1 tw-text-sm tw-text-gray-600">
                                {{ totalTemplateRows }} data rows
                                {{ templateValidation.isValid ? 'are ready to import' : 'found' }}
                                across {{ templateValidation.sheets.length }} sheets.
                            </p>
                            <ul class="tw-mt-2 tw-space-y-2">
                                <li v-for="sheet in templateValidation.sheets" :key="sheet.name" class="tw-text-sm">
                                    <div class="tw-flex tw-items-center tw-justify-between tw-gap-3">
                                        <span>{{ sheet.name }}</span>
                                        <span>
                                            {{ sheet.rows }} rows ·
                                            {{ sheet.isValid ? 'Validated' : 'Needs attention' }}
                                        </span>
                                    </div>
                                    <pre
                                        v-if="!sheet.isValid"
                                        class="tw-mt-1 tw-whitespace-pre-wrap tw-font-sans tw-text-xs tw-text-amber-700"
                                        >{{ sheet.validationMessage }}</pre
                                    >
                                </li>
                            </ul>
                        </template>
                    </template>
                    <p v-else class="tw-mt-3 tw-text-sm tw-text-gray-500 tw-mb-0">
                        Upload a completed .xlsx template above to check its sheets and the rows it contains.
                    </p>
                </WizardPanel>
            </div>
            <div v-else-if="currentStep === 'Departments'" key="departments" class="tw-px-6">
                <span class="tw-block tw-text-lg tw-font-semibold tw-mt-4">Departments &amp; Revenue Streams</span>
                <WizardPanel class="tw-my-5 tw-px-0" header-class="tw-px-5" title="Revenue Streams" collapsible>
                    <table class="table tw-mt-4 !tw-mb-0">
                        <thead>
                            <tr>
                                <th width="20"></th>
                                <th>Name</th>
                                <th width="50%">Description</th>
                                <th width="100"></th>
                            </tr>
                        </thead>
                        <tbody>
                            <template
                                v-for="revenueStream in form.departments.filter((d) => d.type === 2)"
                                :key="revenueStream.id"
                            >
                                <tr @click="revenueStream.expanded = !revenueStream.expanded">
                                    <td
                                        :class="{
                                            'tw-border-l-[3px] !tw-border-l-eventwise': revenueStream.expanded,
                                        }"
                                    >
                                        <i
                                            class="tw-block mdi mdi-chevron-right tw-scale-[1.33] tw-transition-all"
                                            :class="{
                                                'tw-rotate-90 tw-text-eventwise': revenueStream.expanded,
                                            }"
                                        ></i>
                                    </td>
                                    <td>
                                        <EditableText
                                            v-model="revenueStream.name"
                                            :multiline="false"
                                            placeholder="Name (required)"
                                            @click.stop
                                        />
                                    </td>
                                    <td>
                                        <EditableText
                                            v-model="revenueStream.description"
                                            :multiline="false"
                                            placeholder="Description (optional)"
                                            @click.stop
                                        />
                                    </td>
                                    <td>
                                        <div class="tw-flex tw-items-center tw-gap-2 tw-justify-end">
                                            <Dropdown
                                                v-if="isCopyingPreviousEvent && getSavedId(revenueStream.id) !== null"
                                            >
                                                <template #trigger>
                                                    <button
                                                        class="tw-w-7 tw-h-7 tw-flex tw-items-center tw-justify-center tw-rounded-full hover:tw-bg-gray-100"
                                                        @click.stop
                                                    >
                                                        <i class="mdi mdi-content-copy tw-block tw-scale-[1.1]"></i>
                                                    </button>
                                                </template>
                                                <ul>
                                                    <li class="tw-text-xs tw-pt-2 tw-px-2 tw-pb-1 tw-text-gray-400">
                                                        Set for all subcategories
                                                    </li>
                                                    <li>
                                                        <Button
                                                            type="is-dropdown"
                                                            @click="
                                                                revenueStream.categories = revenueStream.categories.map(
                                                                    (c) => {
                                                                        c.copy_pphe = false;
                                                                        c.copy_tickets = false;
                                                                        c.copy_budget = false;
                                                                        c.copy_revenue = false;
                                                                        return c;
                                                                    }
                                                                )
                                                            "
                                                        >
                                                            Don't copy any data
                                                        </Button>
                                                    </li>
                                                    <template v-if="revenueStream.revenue_stream_type === 0">
                                                        <li class="separator"></li>
                                                        <li>
                                                            <Button
                                                                type="is-dropdown"
                                                                @click="
                                                                    revenueStream.categories =
                                                                        revenueStream.categories.map((c) => {
                                                                            c.copy_tickets = true;
                                                                            return c;
                                                                        })
                                                                "
                                                            >
                                                                Copy tickets
                                                            </Button>
                                                        </li>
                                                    </template>
                                                    <template v-if="revenueStream.revenue_stream_type !== 0">
                                                        <li class="separator"></li>
                                                        <li>
                                                            <Button
                                                                type="is-dropdown"
                                                                @click="
                                                                    revenueStream.categories =
                                                                        revenueStream.categories.map((c) => {
                                                                            c.copy_budget = true;
                                                                            c.copy_budget_option = null;
                                                                            return c;
                                                                        })
                                                                "
                                                            >
                                                                Copy budget entries as-is
                                                            </Button>
                                                        </li>
                                                        <li>
                                                            <Button
                                                                type="is-dropdown"
                                                                @click="
                                                                    revenueStream.categories =
                                                                        revenueStream.categories.map((c) => {
                                                                            c.copy_budget = true;
                                                                            c.copy_budget_option = 'single';
                                                                            return c;
                                                                        })
                                                                "
                                                            >
                                                                Copy and merge budget entries
                                                            </Button>
                                                        </li>
                                                        <li class="separator"></li>
                                                        <li>
                                                            <Button
                                                                type="is-dropdown"
                                                                @click="
                                                                    revenueStream.categories =
                                                                        revenueStream.categories.map((c) => {
                                                                            c.copy_revenue = true;
                                                                            c.copy_revenue_option = null;
                                                                            return c;
                                                                        })
                                                                "
                                                            >
                                                                Copy revenue entries as-is
                                                            </Button>
                                                        </li>
                                                        <li>
                                                            <Button
                                                                type="is-dropdown"
                                                                @click="
                                                                    revenueStream.categories =
                                                                        revenueStream.categories.map((c) => {
                                                                            c.copy_revenue = true;
                                                                            c.copy_revenue_option = 'single';
                                                                            return c;
                                                                        })
                                                                "
                                                            >
                                                                Copy and merge revenue entries
                                                            </Button>
                                                        </li>
                                                    </template>
                                                    <template v-if="revenueStream.revenue_stream_type === 1">
                                                        <li class="separator"></li>
                                                        <li>
                                                            <Button
                                                                type="is-dropdown"
                                                                @click="
                                                                    revenueStream.categories =
                                                                        revenueStream.categories.map((c) => {
                                                                            c.copy_pphe = true;
                                                                            return c;
                                                                        })
                                                                "
                                                            >
                                                                Copy price per head entries
                                                            </Button>
                                                        </li>
                                                    </template>
                                                </ul>
                                            </Dropdown>
                                            <Dropdown>
                                                <template #trigger>
                                                    <button
                                                        class="tw-w-7 tw-h-7 tw-flex tw-items-center tw-justify-center tw-rounded-full hover:tw-bg-gray-100"
                                                        @click.stop
                                                    >
                                                        <i
                                                            class="mdi tw-block tw-scale-[1.1]"
                                                            :class="{
                                                                'mdi-cash-multiple':
                                                                    revenueStream.revenue_stream_type === 3,
                                                                'mdi-ticket': revenueStream.revenue_stream_type === 0,
                                                                'mdi-glass-cocktail':
                                                                    revenueStream.revenue_stream_type === 1,
                                                                'mdi-store': revenueStream.revenue_stream_type === 2, // Keep traders type here but not in dropdown for legacy data
                                                            }"
                                                        ></i>
                                                    </button>
                                                </template>
                                                <ul>
                                                    <li>
                                                        <Button
                                                            :active="revenueStream.revenue_stream_type === 3"
                                                            type="is-dropdown"
                                                            @click="revenueStream.revenue_stream_type = 3"
                                                        >
                                                            <i class="mdi mdi-cash-multiple"></i>
                                                            General Revenue
                                                        </Button>
                                                    </li>
                                                    <li>
                                                        <Button
                                                            :active="revenueStream.revenue_stream_type === 0"
                                                            type="is-dropdown"
                                                            @click="revenueStream.revenue_stream_type = 0"
                                                        >
                                                            <i class="mdi mdi-ticket"></i>
                                                            Tickets
                                                        </Button>
                                                    </li>
                                                    <li>
                                                        <Button
                                                            :active="revenueStream.revenue_stream_type === 1"
                                                            type="is-dropdown"
                                                            @click="revenueStream.revenue_stream_type = 1"
                                                        >
                                                            <i class="mdi mdi-glass-cocktail"></i>
                                                            Bars
                                                        </Button>
                                                    </li>
                                                </ul>
                                            </Dropdown>
                                            <Button
                                                type="is-danger"
                                                glass
                                                compact
                                                @click.stop="
                                                    form.departments = form.departments.filter(
                                                        (d) => d.id !== revenueStream.id
                                                    )
                                                "
                                            >
                                                <i class="mdi mdi-close"></i>
                                            </Button>
                                        </div>
                                    </td>
                                </tr>
                                <tr>
                                    <td
                                        v-if="revenueStream.expanded"
                                        colspan="4"
                                        class="tw-bg-slate-50 tw-border-l-[3px] !tw-border-l-eventwise !tw-p-0"
                                    >
                                        <table class="table !tw-mb-0">
                                            <tbody>
                                                <tr v-for="category in revenueStream.categories" :key="category.id">
                                                    <td>
                                                        <EditableText
                                                            v-model="category.name"
                                                            :multiline="false"
                                                            placeholder="Name (required)"
                                                        />
                                                    </td>
                                                    <td>
                                                        <EditableText
                                                            v-model="category.description"
                                                            :multiline="false"
                                                            placeholder="Description (optional)"
                                                        />
                                                    </td>
                                                    <td class="tw-text-right" width="40">
                                                        <div class="tw-flex tw-items-center tw-justify-end tw-gap-2">
                                                            <Dropdown
                                                                v-if="
                                                                    isCopyingPreviousEvent &&
                                                                    getSavedId(category.id) !== null
                                                                "
                                                            >
                                                                <template #trigger>
                                                                    <button
                                                                        class="tw-w-7 tw-h-7 tw-flex tw-items-center tw-justify-center tw-rounded-full hover:tw-bg-gray-100"
                                                                        :class="{
                                                                            '!tw-bg-eventwise !tw-text-white':
                                                                                category.copy_tickets ||
                                                                                category.copy_budget ||
                                                                                category.copy_revenue ||
                                                                                category.copy_pphe,
                                                                        }"
                                                                        @click.stop
                                                                    >
                                                                        <i
                                                                            class="mdi mdi-content-copy tw-block tw-scale-[1.1]"
                                                                        ></i>
                                                                    </button>
                                                                </template>
                                                                <ul>
                                                                    <template
                                                                        v-if="revenueStream.revenue_stream_type === 0"
                                                                    >
                                                                        <li>
                                                                            <Button
                                                                                :active="!category.copy_tickets"
                                                                                type="is-dropdown"
                                                                                @click="category.copy_tickets = false"
                                                                            >
                                                                                Don't copy any tickets
                                                                            </Button>
                                                                        </li>
                                                                        <li>
                                                                            <Button
                                                                                :active="category.copy_tickets"
                                                                                type="is-dropdown"
                                                                                @click="category.copy_tickets = true"
                                                                            >
                                                                                Copy tickets
                                                                            </Button>
                                                                        </li>
                                                                    </template>
                                                                    <template
                                                                        v-if="revenueStream.revenue_stream_type !== 0"
                                                                    >
                                                                        <li>
                                                                            <Button
                                                                                :active="!category.copy_budget"
                                                                                type="is-dropdown"
                                                                                @click="category.copy_budget = false"
                                                                            >
                                                                                Don't copy any budget entries
                                                                            </Button>
                                                                        </li>
                                                                        <li>
                                                                            <Button
                                                                                :active="
                                                                                    category.copy_budget &&
                                                                                    !category.copy_budget_option
                                                                                "
                                                                                type="is-dropdown"
                                                                                @click="
                                                                                    category.copy_budget = true;
                                                                                    category.copy_budget_option = null;
                                                                                "
                                                                            >
                                                                                Copy budget entries as-is
                                                                            </Button>
                                                                        </li>
                                                                        <li>
                                                                            <Button
                                                                                :active="
                                                                                    category.copy_budget &&
                                                                                    category.copy_budget_option ===
                                                                                        'single'
                                                                                "
                                                                                type="is-dropdown"
                                                                                @click="
                                                                                    category.copy_budget = true;
                                                                                    category.copy_budget_option =
                                                                                        'single';
                                                                                "
                                                                            >
                                                                                Copy and merge budget entries
                                                                            </Button>
                                                                        </li>
                                                                        <li class="separator"></li>
                                                                        <li>
                                                                            <Button
                                                                                :active="!category.copy_revenue"
                                                                                type="is-dropdown"
                                                                                @click="category.copy_revenue = false"
                                                                            >
                                                                                Don't copy any revenue entries
                                                                            </Button>
                                                                        </li>
                                                                        <li>
                                                                            <Button
                                                                                :active="
                                                                                    category.copy_revenue &&
                                                                                    !category.copy_revenue_option
                                                                                "
                                                                                type="is-dropdown"
                                                                                @click="
                                                                                    category.copy_revenue = true;
                                                                                    category.copy_revenue_option = null;
                                                                                "
                                                                            >
                                                                                Copy revenue entries as-is
                                                                            </Button>
                                                                        </li>
                                                                        <li>
                                                                            <Button
                                                                                :active="
                                                                                    category.copy_revenue &&
                                                                                    category.copy_revenue_option ===
                                                                                        'single'
                                                                                "
                                                                                type="is-dropdown"
                                                                                @click="
                                                                                    category.copy_revenue = true;
                                                                                    category.copy_revenue_option =
                                                                                        'single';
                                                                                "
                                                                            >
                                                                                Copy and merge revenue entries
                                                                            </Button>
                                                                        </li>
                                                                    </template>
                                                                    <template
                                                                        v-if="revenueStream.revenue_stream_type === 1"
                                                                    >
                                                                        <li class="separator"></li>
                                                                        <li>
                                                                            <Button
                                                                                :active="!category.copy_pphe"
                                                                                type="is-dropdown"
                                                                                @click="category.copy_pphe = false"
                                                                            >
                                                                                Don't copy any price per head entries
                                                                            </Button>
                                                                        </li>
                                                                        <li>
                                                                            <Button
                                                                                :active="category.copy_pphe"
                                                                                type="is-dropdown"
                                                                                @click="category.copy_pphe = true"
                                                                            >
                                                                                Copy price per head entries
                                                                            </Button>
                                                                        </li>
                                                                    </template>
                                                                </ul>
                                                            </Dropdown>
                                                            <Button
                                                                type="is-danger"
                                                                glass
                                                                compact
                                                                @click="
                                                                    revenueStream.categories =
                                                                        revenueStream.categories.filter(
                                                                            (c) => c.id !== category.id
                                                                        )
                                                                "
                                                            >
                                                                <i class="mdi mdi-close"></i>
                                                            </Button>
                                                        </div>
                                                    </td>
                                                </tr>
                                                <tr>
                                                    <td>
                                                        <Autocomplete
                                                            v-if="
                                                                addingExistingSubcategoryForDepartment ===
                                                                String(revenueStream.id)
                                                            "
                                                            :endpoint="
                                                                route('api.account.departments.categories', {
                                                                    account: accountId,
                                                                    department: revenueStream.id,
                                                                    paginated: 1,
                                                                    exclude: getExistingSubcategoryExclusions(
                                                                        revenueStream.categories
                                                                    ),
                                                                })
                                                            "
                                                            placeholder="Select an existing subcategory"
                                                            @selection-change="
                                                                (category) =>
                                                                    setSelectedExistingSubcategory(
                                                                        revenueStream.id,
                                                                        category
                                                                    )
                                                            "
                                                        />
                                                        <EditableText
                                                            v-else
                                                            v-model="revenueStream.newCategoryName"
                                                            placeholder="Type to add a Category"
                                                            :multiline="false"
                                                            @focusout.native="revenueStream.newCategoryName = ''"
                                                            @keyup.enter.native="
                                                                handleAddCategory(
                                                                    revenueStream.id,
                                                                    revenueStream.newCategoryName
                                                                )
                                                            "
                                                        />
                                                    </td>
                                                    <td colspan="2" width="54%">
                                                        <div
                                                            v-if="
                                                                addingExistingSubcategoryForDepartment ===
                                                                String(revenueStream.id)
                                                            "
                                                            class="tw-flex tw-items-center tw-gap-2"
                                                        >
                                                            <Button
                                                                :disabled="
                                                                    !selectedExistingSubcategories[
                                                                        String(revenueStream.id)
                                                                    ]
                                                                "
                                                                @click="handleAddExistingSubcategory(revenueStream)"
                                                            >
                                                                Add
                                                            </Button>
                                                            <Button
                                                                type="is-link"
                                                                @click="cancelAddingExistingSubcategory()"
                                                            >
                                                                Cancel
                                                            </Button>
                                                        </div>
                                                        <div
                                                            v-else
                                                            class="tw-flex tw-items-center tw-justify-between tw-gap-2"
                                                        >
                                                            <span
                                                                v-if="!!revenueStream.newCategoryName"
                                                                class="tw-flex tw-items-center tw-text-gray-400"
                                                            >
                                                                Press
                                                                <span
                                                                    class="tw-p-1 tw-mx-1 tw-border tw-rounded tw-text-xs"
                                                                >
                                                                    ENTER
                                                                </span>
                                                                to save, click elsewhere to reset
                                                            </span>
                                                            <Button
                                                                v-if="getSavedId(revenueStream.id) !== null"
                                                                type="is-link"
                                                                @click.stop="
                                                                    startAddingExistingSubcategory(revenueStream.id)
                                                                "
                                                            >
                                                                Add an existing subcategory
                                                            </Button>
                                                        </div>
                                                    </td>
                                                </tr>
                                            </tbody>
                                        </table>
                                    </td>
                                </tr>
                            </template>
                            <tr>
                                <td class="!tw-pb-0"></td>
                                <td class="!tw-pb-0">
                                    <EditableText
                                        v-if="!addingExistingRevenueStream"
                                        v-model="creatingRevenueStream"
                                        placeholder="Type to create a new revenue stream"
                                        :multiline="false"
                                        @focusout.native="creatingRevenueStream = ''"
                                        @keyup.enter.native="
                                            handleAddDepartment(creatingRevenueStream, {
                                                type: 2,
                                                revenue_stream_type: 3,
                                            })
                                        "
                                    />
                                    <Autocomplete
                                        v-else
                                        ref="existingRevenueStreamSelector"
                                        :endpoint="
                                            route('api.account.departments', {
                                                account: accountId,
                                                type: 2,
                                                paginated: 1,
                                                exclude: form.departments.length
                                                    ? ['departments.id=' + form.departments.map((d) => d.id).join(',')]
                                                    : [],
                                            })
                                        "
                                        placeholder="Select an existing revenue stream"
                                    />
                                </td>
                                <td class="!tw-pb-0" colspan="2">
                                    <span
                                        v-if="!!creatingRevenueStream"
                                        class="tw-flex tw-items-center tw-text-gray-400"
                                    >
                                        Press
                                        <span class="tw-p-1 tw-mx-1 tw-border tw-rounded tw-text-xs">ENTER</span>
                                        to save, click elsewhere to reset
                                    </span>
                                    <Button
                                        v-else-if="!addingExistingRevenueStream"
                                        type="is-link"
                                        @click="addingExistingRevenueStream = true"
                                    >
                                        Add an existing revenue stream
                                    </Button>
                                    <div v-else class="tw-flex tw-items-center">
                                        <Button @click="handleAddExistingRevenueStream()">Add</Button>
                                        <Button type="is-link" @click="addingExistingRevenueStream = false">
                                            Cancel
                                        </Button>
                                    </div>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </WizardPanel>
                <WizardPanel class="tw-my-5 tw-px-0" header-class="tw-px-5" title="Cost Departments" collapsible>
                    <table class="table tw-mt-4 !tw-mb-0">
                        <thead>
                            <tr>
                                <th width="20"></th>
                                <th>Name</th>
                                <th width="50%">Description</th>
                                <th width="100"></th>
                            </tr>
                        </thead>
                        <tbody>
                            <template
                                v-for="department in form.departments.filter((d) => d.type === 1)"
                                :key="department.id"
                            >
                                <tr @click="department.expanded = !department.expanded">
                                    <td
                                        :class="{
                                            'tw-border-l-[3px] !tw-border-l-eventwise': department.expanded,
                                        }"
                                    >
                                        <i
                                            class="tw-block mdi mdi-chevron-right tw-scale-[1.33] tw-transition-all"
                                            :class="{
                                                'tw-rotate-90 tw-text-eventwise': department.expanded,
                                            }"
                                        ></i>
                                    </td>
                                    <td>
                                        <EditableText
                                            v-model="department.name"
                                            :multiline="false"
                                            placeholder="Name (required)"
                                            @click.stop
                                        />
                                    </td>
                                    <td>
                                        <EditableText
                                            v-model="department.description"
                                            :multiline="false"
                                            placeholder="Description (optional)"
                                            @click.stop
                                        />
                                    </td>
                                    <td>
                                        <div class="tw-flex tw-items-center tw-justify-end tw-gap-2">
                                            <Dropdown
                                                v-if="isCopyingPreviousEvent && getSavedId(department.id) !== null"
                                            >
                                                <template #trigger>
                                                    <button
                                                        class="tw-w-7 tw-h-7 tw-flex tw-items-center tw-justify-center tw-rounded-full hover:tw-bg-gray-100"
                                                        @click.stop
                                                    >
                                                        <i class="mdi mdi-content-copy tw-block tw-scale-[1.1]"></i>
                                                    </button>
                                                </template>
                                                <ul>
                                                    <li class="tw-text-xs tw-pt-2 tw-px-2 tw-pb-1 tw-text-gray-400">
                                                        Set for all subcategories
                                                    </li>
                                                    <li>
                                                        <Button
                                                            type="is-dropdown"
                                                            @click="
                                                                department.categories = department.categories.map(
                                                                    (c) => {
                                                                        c.copy_budget = false;
                                                                        return c;
                                                                    }
                                                                )
                                                            "
                                                        >
                                                            Don't copy any data
                                                        </Button>
                                                    </li>
                                                    <li class="separator"></li>
                                                    <li>
                                                        <Button
                                                            type="is-dropdown"
                                                            @click="
                                                                department.categories = department.categories.map(
                                                                    (c) => {
                                                                        c.copy_budget = true;
                                                                        c.copy_budget_option = null;
                                                                        return c;
                                                                    }
                                                                )
                                                            "
                                                        >
                                                            Copy budget entries as-is
                                                        </Button>
                                                    </li>
                                                    <li>
                                                        <Button
                                                            type="is-dropdown"
                                                            @click="
                                                                department.categories = department.categories.map(
                                                                    (c) => {
                                                                        c.copy_budget = true;
                                                                        c.copy_budget_option = 'single';
                                                                        return c;
                                                                    }
                                                                )
                                                            "
                                                        >
                                                            Copy and merge budget entries
                                                        </Button>
                                                    </li>
                                                </ul>
                                            </Dropdown>
                                            <Button
                                                type="is-danger"
                                                glass
                                                compact
                                                @click.stop="
                                                    form.departments = form.departments.filter(
                                                        (d) => d.id !== department.id
                                                    )
                                                "
                                            >
                                                <i class="mdi mdi-close"></i>
                                            </Button>
                                        </div>
                                    </td>
                                </tr>
                                <tr>
                                    <td
                                        v-if="department.expanded"
                                        colspan="4"
                                        class="tw-bg-slate-50 tw-border-l-[3px] !tw-border-l-eventwise !tw-p-0"
                                    >
                                        <table class="table !tw-mb-0">
                                            <tbody>
                                                <tr v-for="category in department.categories" :key="category.id">
                                                    <td>
                                                        <EditableText
                                                            v-model="category.name"
                                                            :multiline="false"
                                                            placeholder="Name (required)"
                                                        />
                                                    </td>
                                                    <td>
                                                        <EditableText
                                                            v-model="category.description"
                                                            :multiline="false"
                                                            placeholder="Description (optional)"
                                                        />
                                                    </td>
                                                    <td class="tw-text-right" width="40">
                                                        <div class="tw-flex tw-items-center tw-justify-end tw-gap-2">
                                                            <Dropdown
                                                                v-if="
                                                                    isCopyingPreviousEvent &&
                                                                    getSavedId(category.id) !== null
                                                                "
                                                            >
                                                                <template #trigger>
                                                                    <button
                                                                        class="tw-w-7 tw-h-7 tw-flex tw-items-center tw-justify-center tw-rounded-full hover:tw-bg-gray-100"
                                                                        :class="{
                                                                            '!tw-bg-eventwise !tw-text-white':
                                                                                category.copy_tickets ||
                                                                                category.copy_budget ||
                                                                                category.copy_revenue ||
                                                                                category.copy_pphe,
                                                                        }"
                                                                        @click.stop
                                                                    >
                                                                        <i
                                                                            class="mdi mdi-content-copy tw-block tw-scale-[1.1]"
                                                                        ></i>
                                                                    </button>
                                                                </template>
                                                                <ul>
                                                                    <li>
                                                                        <Button
                                                                            :active="!category.copy_budget"
                                                                            type="is-dropdown"
                                                                            @click="category.copy_budget = false"
                                                                        >
                                                                            Don't copy any budget entries
                                                                        </Button>
                                                                    </li>
                                                                    <li>
                                                                        <Button
                                                                            :active="
                                                                                category.copy_budget &&
                                                                                !category.copy_budget_option
                                                                            "
                                                                            type="is-dropdown"
                                                                            @click="
                                                                                category.copy_budget = true;
                                                                                category.copy_budget_option = null;
                                                                            "
                                                                        >
                                                                            Copy budget entries as-is
                                                                        </Button>
                                                                    </li>
                                                                    <li>
                                                                        <Button
                                                                            :active="
                                                                                category.copy_budget &&
                                                                                category.copy_budget_option === 'single'
                                                                            "
                                                                            type="is-dropdown"
                                                                            @click="
                                                                                category.copy_budget = true;
                                                                                category.copy_budget_option = 'single';
                                                                            "
                                                                        >
                                                                            Copy and merge budget entries
                                                                        </Button>
                                                                    </li>
                                                                </ul>
                                                            </Dropdown>
                                                            <Button
                                                                type="is-danger"
                                                                glass
                                                                compact
                                                                @click="
                                                                    department.categories =
                                                                        department.categories.filter(
                                                                            (c) => c.id !== category.id
                                                                        )
                                                                "
                                                            >
                                                                <i class="mdi mdi-close"></i>
                                                            </Button>
                                                        </div>
                                                    </td>
                                                </tr>
                                                <tr>
                                                    <td>
                                                        <Autocomplete
                                                            v-if="
                                                                addingExistingSubcategoryForDepartment ===
                                                                String(department.id)
                                                            "
                                                            :endpoint="
                                                                route('api.account.departments.categories', {
                                                                    account: accountId,
                                                                    department: department.id,
                                                                    paginated: 1,
                                                                    exclude: getExistingSubcategoryExclusions(
                                                                        department.categories
                                                                    ),
                                                                })
                                                            "
                                                            placeholder="Select an existing subcategory"
                                                            @selection-change="
                                                                (category) =>
                                                                    setSelectedExistingSubcategory(
                                                                        department.id,
                                                                        category
                                                                    )
                                                            "
                                                        />
                                                        <EditableText
                                                            v-else
                                                            v-model="department.newCategoryName"
                                                            placeholder="Type to add a Category"
                                                            :multiline="false"
                                                            @focusout.native="department.newCategoryName = ''"
                                                            @keyup.enter.native="
                                                                handleAddCategory(
                                                                    department.id,
                                                                    department.newCategoryName
                                                                )
                                                            "
                                                        />
                                                    </td>
                                                    <td colspan="2" width="54%">
                                                        <div
                                                            v-if="
                                                                addingExistingSubcategoryForDepartment ===
                                                                String(department.id)
                                                            "
                                                            class="tw-flex tw-items-center tw-gap-2"
                                                        >
                                                            <Button
                                                                :disabled="
                                                                    !selectedExistingSubcategories[
                                                                        String(department.id)
                                                                    ]
                                                                "
                                                                @click="handleAddExistingSubcategory(department)"
                                                            >
                                                                Add
                                                            </Button>
                                                            <Button
                                                                type="is-link"
                                                                @click="cancelAddingExistingSubcategory()"
                                                            >
                                                                Cancel
                                                            </Button>
                                                        </div>
                                                        <div
                                                            v-else
                                                            class="tw-flex tw-items-center tw-justify-between tw-gap-2"
                                                        >
                                                            <span
                                                                v-if="!!department.newCategoryName"
                                                                class="tw-flex tw-items-center tw-text-gray-400"
                                                            >
                                                                Press
                                                                <span
                                                                    class="tw-p-1 tw-mx-1 tw-border tw-rounded tw-text-xs"
                                                                >
                                                                    ENTER
                                                                </span>
                                                                to save, click elsewhere to reset
                                                            </span>
                                                            <Button
                                                                v-if="getSavedId(department.id) !== null"
                                                                type="is-link"
                                                                @click.stop="
                                                                    startAddingExistingSubcategory(department.id)
                                                                "
                                                            >
                                                                Add an existing subcategory
                                                            </Button>
                                                        </div>
                                                    </td>
                                                </tr>
                                            </tbody>
                                        </table>
                                    </td>
                                </tr>
                            </template>
                            <tr>
                                <td class="!tw-pb-0"></td>
                                <td class="!tw-pb-0">
                                    <EditableText
                                        v-if="!addingExistingDepartment"
                                        v-model="creatingDepartment"
                                        placeholder="Type to create a new department"
                                        :multiline="false"
                                        @focusout.native="creatingDepartment = ''"
                                        @keyup.enter.native="handleAddDepartment(creatingDepartment, { type: 1 })"
                                    />
                                    <Autocomplete
                                        v-else
                                        ref="existingDepartmentSelector"
                                        :endpoint="
                                            route('api.account.departments', {
                                                account: accountId,
                                                type: 1,
                                                paginated: 1,
                                                exclude: form.departments.length
                                                    ? ['departments.id=' + form.departments.map((d) => d.id).join(',')]
                                                    : [],
                                            })
                                        "
                                        placeholder="Select an existing department"
                                    />
                                </td>
                                <td class="!tw-pb-0" colspan="2">
                                    <span v-if="!!creatingDepartment" class="tw-flex tw-items-center tw-text-gray-400">
                                        Press
                                        <span class="tw-p-1 tw-mx-1 tw-border tw-rounded tw-text-xs">ENTER</span>
                                        to save, click elsewhere to reset
                                    </span>
                                    <Button
                                        v-else-if="!addingExistingDepartment"
                                        type="is-link"
                                        @click="addingExistingDepartment = true"
                                    >
                                        Add an existing department
                                    </Button>
                                    <div v-else class="tw-flex tw-items-center">
                                        <Button @click="handleAddExistingDepartment()">Add</Button>
                                        <Button type="is-link" @click="addingExistingDepartment = false">Cancel</Button>
                                    </div>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </WizardPanel>
            </div>
            <div
                v-else-if="currentStep === 'Review'"
                key="review"
                class="tw-px-6 tw-grid tw-grid-cols-2 tw-gap-3 tw-pt-5"
            >
                <div class="tw-flex tw-flex-col tw-gap-3 tw-pb-3">
                    <Banner v-if="!form.project_start" type="is-danger" icon="mdi-alert">
                        Your event doesn&apos;t have a valid project start date
                    </Banner>
                    <Banner v-if="!form.event_dates" type="is-danger" icon="mdi-alert">
                        Your event doesn&apos;t have valid event dates set
                    </Banner>
                    <Banner v-if="!isAddressValid" type="is-danger" icon="mdi-alert">
                        Select a venue with address line 1, city, postcode and country
                    </Banner>
                    <Banner v-if="eventCreationError" type="is-danger" icon="mdi-alert">
                        {{ eventCreationError }}
                    </Banner>
                    <Banner v-if="!recurringOptionsAreValid" type="is-danger" icon="mdi-alert">
                        Each additional event needs a unique name, project start date, and event date(s).
                    </Banner>
                    <WizardPanel title="Event Details" content-class="tw-flex tw-flex-col tw-gap-2 tw-pt-3">
                        <FormField label="Event Name">
                            {{ form.name }}
                        </FormField>
                        <FormField label="Project Start">
                            {{ form.project_start }}
                        </FormField>
                        <FormField :label="form.event_dates.includes('to') ? 'Event Dates' : 'Event Date'">
                            {{ form.event_dates }}
                        </FormField>
                        <FormField label="Venue">
                            {{ form.primary_address?.address ?? '' }}
                        </FormField>
                        <FormField label="Primary Currency">
                            {{ form.currency_code }}
                        </FormField>
                        <FormField label="Tags">
                            <span v-if="form.tags.length === 0" class="tw-text-gray-400">No tags have been added</span>
                            <span v-else>{{ form.tags.length }} selected</span>
                        </FormField>
                    </WizardPanel>
                    <WizardPanel title="Team Members">
                        <div class="tw-mt-3 tw-flex tw-gap-3">
                            <User
                                v-for="user in accountUsers.filter((accountUser) =>
                                    form.users.includes(accountUser.id)
                                )"
                                :key="user.id"
                                :user="user"
                            />
                        </div>
                    </WizardPanel>
                    <WizardPanel title="Event Series">
                        <span v-if="!form.is_recurring" class="tw-text-gray-400">
                            A series was not configured for this event.
                        </span>
                        <span v-else-if="!form.recurring_options.length" class="tw-text-gray-400">
                            No additional events have been added to the series.
                        </span>
                        <div v-else class="tw-mt-3">
                            <p class="tw-mb-3">
                                Each event will use the same venue, team, currency, tags, and department setup.
                            </p>
                            <table class="table !tw-mb-0">
                                <thead>
                                    <tr>
                                        <th>Event Name</th>
                                        <th>Project Start</th>
                                        <th>Event Dates</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr v-for="(recurringOption, index) in form.recurring_options" :key="index">
                                        <td>{{ recurringOption.name }}</td>
                                        <td>{{ recurringOption.project_start }}</td>
                                        <td>{{ recurringOption.event_dates }}</td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </WizardPanel>
                </div>
                <div class="tw-flex tw-flex-col tw-gap-3 tw-pb-3">
                    <Banner
                        v-if="!!form.departments.filter((d) => d.type === 2 && !d.categories.length).length"
                        type="is-danger"
                        icon="mdi-alert"
                    >
                        At least one of your revenue streams doesn&apos;t have any subcategories assigned
                    </Banner>
                    <WizardPanel title="Revenue Streams" header-class="tw-pl-5" class="!tw-px-0 !tw-pb-0">
                        <table class="table tw-mt-3 !tw-mb-0">
                            <tbody>
                                <tr
                                    v-for="revenueStream in form.departments.filter((d) => d.type === 2)"
                                    :key="revenueStream.id"
                                >
                                    <td class="!tw-px-5">{{ revenueStream.name }}</td>
                                    <td class="tw-text-right tw-text-gray-400">
                                        {{ revenueStream.categories.length }} subcategor{{
                                            revenueStream.categories.length === 1 ? 'y' : 'ies'
                                        }}
                                    </td>
                                </tr>
                                <tr v-if="!form.departments.filter((d) => d.type === 2).length">
                                    <td class="tw-text-gray-400 !tw-px-5">
                                        You haven&apos;t set up any revenue streams
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </WizardPanel>
                    <Banner
                        v-if="!!form.departments.filter((d) => d.type === 1 && !d.categories.length).length"
                        type="is-danger"
                        icon="mdi-alert"
                    >
                        At least one of your cost departments doesn&apos;t have any subcategories assigned
                    </Banner>
                    <WizardPanel title="Cost Departments" header-class="tw-pl-5" class="!tw-px-0 !tw-pb-0">
                        <table class="table tw-mt-3 !tw-mb-0">
                            <tbody>
                                <tr
                                    v-for="costDepartment in form.departments.filter((d) => d.type === 1)"
                                    :key="costDepartment.id"
                                >
                                    <td class="!tw-px-5">{{ costDepartment.name }}</td>
                                    <td class="tw-text-right tw-text-gray-400">
                                        {{ costDepartment.categories.length }} subcategor{{
                                            costDepartment.categories.length === 1 ? 'y' : 'ies'
                                        }}
                                    </td>
                                </tr>
                                <tr v-if="!form.departments.filter((d) => d.type === 1).length">
                                    <td class="tw-text-gray-400 !tw-px-5">
                                        You haven&apos;t set up any cost departments
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </WizardPanel>
                </div>
            </div>
        </Transition>

        <Teleport to="body">
            <CreateVenueModal
                ref="createVenueModal"
                :account-id="accountId"
                :locales="locales"
                @created="handleVenueCreated"
            />
        </Teleport>

        <template #footer>
            <div class="tw-flex tw-justify-between">
                <Button
                    :loading="isLoading"
                    :disabled="availableSteps.indexOf(currentStep) === 0 || createdEventIds.length > 0"
                    @click="wizard?.retreat()"
                >
                    Previous
                </Button>
                <Button
                    v-if="currentStep !== 'Review'"
                    :loading="isLoading"
                    :disabled="currentStep === 'Select Template' && !templateValidation?.isValid"
                    @click="wizard?.advance()"
                >
                    Next
                </Button>
                <Button v-else :disabled="!isValid" :loading="isLoading" @click="createEvent()">
                    {{ createButtonLabel }}
                </Button>
            </div>
        </template>
    </FullModal>
</template>
<script setup lang="ts">
import FullModal from '@/js/components/FullModal.vue';
import CreateVenueModal from '@/js/components/EventWizard/CreateVenueModal.vue';
import WizardSteps from '@/js/components/widgets/WizardSteps.vue';
import WizardPanel from '@/js/components/EventWizard/WizardPanel.vue';
import Button from '@/js/components/controls/Button.vue';
import RadioBox from '@/js/components/controls/RadioBox.vue';
import FormField from '@/js/components/widgets/FormField.vue';
import Textbox from '@/js/components/controls/Textbox.vue';
import Autocomplete from '@/js/components/Autocomplete.vue';
import VueFlatPickr from 'vue-flatpickr-component';
import CurrencySelect from '@/js/components/controls/CurrencySelect.vue';
import TagSelector from '@/js/components/controls/TagSelector.vue';
import TeamSelector from '@/js/components/controls/TeamSelector.vue';
import TeamMemberUser from '@/js/components/widgets/User.vue';
import GroupTag from '@/js/components/GroupTag.vue';
import Checkbox from '@/js/components/controls/Checkbox.vue';
import NumberInput from '@/js/components/controls/NumberInput.vue';
import EditableText from '@/js/components/controls/EditableText.vue';
import Banner from '@/js/components/Layout/Banner.vue';
import Dropdown from '@/js/components/controls/Dropdown.vue';
import Upload from '@/js/components/controls/Upload.vue';
import { onMounted, onUnmounted, useTemplateRef, ref, computed, watch } from 'vue';
import { route } from 'ziggy-js';
import { useDataStore } from '@/js/stores/DataStore.js';
import { Auth, flatpickrConfig, localTimestamp } from '@/js/utils.js';
import flatpickr from 'flatpickr';
import { debounce, uniqueId } from 'lodash';
import User from '@/js/components/widgets/User.vue';
import axios from 'axios';
import { Eventbus } from '@/js/utils/eventbus.ts';
import { capturePosthogEvent } from '@/js/composables/usePosthog.js';
import type { Address } from '@/js/types/address.ts';
import type { Category } from '@/js/types/category.ts';
import type { Department } from '@/js/types/department.ts';
import type { Event } from '@/js/types/event.ts';

type Override<T, Overrides> = Omit<T, keyof Overrides> & Overrides;

type RecurringOption = Pick<Event, 'name'> & {
    project_start: string | null;
    project_start_default: string | null;
    event_dates: string | null;
};

type EventSetupDetails = Pick<Event, 'name'> & {
    project_start: string | null;
    event_dates: string | null;
};

type VenueOption = Record<string, unknown> &
    Override<
        Pick<Address, 'id' | 'name' | 'address1' | 'address2' | 'city' | 'postcode' | 'country' | 'address'>,
        { name: string }
    >;

type EventCloneCategory = Pick<Category, 'id' | 'name' | 'description'>;

type EventCloneDepartment = Pick<Department, 'id' | 'name' | 'description' | 'revenue_stream_type'> & {
    categories: EventCloneCategory[];
};

type EventCloneDetails = Override<
    Required<
        Pick<
            Event,
            | 'id'
            | 'name'
            | 'primary_address_id'
            | 'primary_address'
            | 'starts_at'
            | 'ends_at'
            | 'project_start'
            | 'currency_code'
            | 'users'
            | 'assigned_tags'
            | 'revenue_departments'
            | 'cost_departments'
        >
    >,
    {
        users: Array<{ id: number | string }>;
        revenue_departments: EventCloneDepartment[];
        cost_departments: EventCloneDepartment[];
    }
>;

type ExistingSubcategoryOption = Override<
    Pick<Category, 'id' | 'name' | 'description'>,
    { id: Category['id'] | string; description: string }
>;

type EventWizardCategory = Override<
    Pick<Category, 'id' | 'name' | 'description'>,
    {
        id: Category['id'] | string;
        description?: string;
        copy_tickets?: boolean;
        copy_budget?: boolean;
        copy_revenue?: boolean;
        copy_pphe?: boolean;
        copy_budget_option?: string | null;
        copy_revenue_option?: string | null;
        xero_account_id?: number | string | null;
    }
>;

type EventWizardDepartment = Override<
    Pick<Department, 'id' | 'type' | 'name' | 'description' | 'revenue_stream_type' | 'stream_type'>,
    {
        id: Department['id'] | string;
        description: NonNullable<Department['description']>;
        revenue_stream_type?: Department['revenue_stream_type'] | string;
        stream_type?: Department['stream_type'] | string;
        categories: EventWizardCategory[];
        newCategoryName: string;
        expanded?: boolean;
    }
>;

type DepartmentCreationAttributes = Override<
    Partial<Pick<Department, 'id' | 'type' | 'description' | 'revenue_stream_type' | 'stream_type'>>,
    {
        id?: Department['id'] | string;
        type: Department['type'];
        revenue_stream_type?: Department['revenue_stream_type'] | string;
        stream_type?: Department['stream_type'] | string;
        categories?: EventWizardCategory[];
    }
>;

type ExistingDepartmentOption = Override<
    Pick<Department, 'id' | 'type' | 'name' | 'description' | 'revenue_stream_type'>,
    {
        id: Department['id'] | string;
        revenue_stream_type?: Department['revenue_stream_type'] | string;
        categories: EventWizardCategory[];
    }
>;

type EventListItem = Pick<Event, 'id' | 'name' | 'primary_address' | 'starts_at' | 'ends_at'>;

const modal = useTemplateRef('modal');
const wizard = useTemplateRef('wizard');
const venueAutocomplete = useTemplateRef('venueAutocomplete');
const createVenueModal = useTemplateRef('createVenueModal');
const existingRevenueStreamSelector = useTemplateRef('existingRevenueStreamSelector');
const existingDepartmentSelector = useTemplateRef('existingDepartmentSelector');

const flatpickrMultiConfig = {
    ...flatpickrConfig,
    mode: 'range' as const,
};

const currentStep = ref<string>('Choose Method');
const isLoading = ref<boolean>(false);
const templateFile = ref<File | null>(null);
const templateValidationLoading = ref<boolean>(false);
const templateValidationError = ref<string>('');
const templateValidation = ref<TemplateValidation | null>(null);
const createdEventIds = ref<number[]>([]);
const importedTemplateEventIds = ref<number[]>([]);
const eventCreationError = ref<string>('');
const eventPrefillError = ref<string>('');
const mainProjectStartDefault = ref<string | null>(null);
const locales = (window as unknown as Window & { Locales: string[] }).Locales;

const accountUsers = computed(() => (useDataStore().get('account.users') ?? []) as Array<{ id: number }>);
const createMethod = ref<'quick' | 'template' | 'copy' | 'ai'>('quick');
const currentUser = useDataStore().get('current_user');
const accountId = Number(useDataStore().get('account.id'));
const form = ref({
    name: '',
    primary_address_id: null as number | string | null,
    primary_address: null as Address | null,
    event_dates: '',
    project_start: '',
    currency_code: useDataStore().get('account.currency_code') ?? 'GBP',
    cloned_event: null as number | null,
    tags: [] as number[],
    users: currentUser?.id ? [Number(currentUser.id)] : ([] as number[]),
    // TODO - do something with this
    is_recurring: false,
    recurring_options: [] as RecurringOption[],
    departments: [] as EventWizardDepartment[],
});

const selectCreateMethod = (method: 'quick' | 'template' | 'copy' | 'ai'): void => {
    if (createMethod.value === method) {
        return;
    }

    createMethod.value = method;

    if (method === 'copy') {
        capturePosthogEvent('event_copy_started');
    } else if (method === 'template') {
        capturePosthogEvent('event_template_selected');
    }
};

type TemplateValidationSheet = {
    name: string;
    rows: number;
    isValid: boolean;
    validationMessage: string;
};

type TemplateValidation = {
    isValid: boolean;
    sheets: TemplateValidationSheet[];
};

type TemplateDepartmentSetup = {
    id: number | null;
    type: 1 | 2;
    name: string;
    description: string;
    revenue_stream_type: number | null;
    categories: Array<{
        id: number | null;
        name: string;
        description: string;
    }>;
};

type TemplateValidationResponse = {
    data: string[];
    sheets: Array<{
        name: string;
        rows: number;
    }>;
    departments: TemplateDepartmentSetup[] | null;
};

let templateValidationRequestId = 0;
const totalTemplateRows = computed(
    () => templateValidation.value?.sheets.reduce((total, sheet) => total + sheet.rows, 0) ?? 0
);
const isAddressValid = computed(() => {
    const address = form.value.primary_address;
    return Boolean(
        form.value.primary_address_id &&
        address &&
        [address.address1, address.city, address.postcode, address.country].every(
            (value) => typeof value === 'string' && value.trim() !== ''
        )
    );
});

watch(templateFile, (file) => {
    const requestId = ++templateValidationRequestId;
    templateValidation.value = null;
    templateValidationError.value = '';
    form.value.departments = [];

    if (!file) {
        templateValidationLoading.value = false;
        return;
    }

    void validateTemplate(file, requestId);
});

const downloadTemplate = (): void => {
    window.open(route('onboarding.template.download'), '_blank');
};

const validateTemplate = async (file: File, requestId: number): Promise<void> => {
    templateValidationLoading.value = true;
    const formData = new FormData();
    formData.append('file', file);

    try {
        const { data: response } = await axios.post<TemplateValidationResponse>(
            route('api.onboarding.import.check', {
                account: accountId,
            }),
            formData
        );

        if (requestId !== templateValidationRequestId) {
            return;
        }

        form.value.departments = (response.departments ?? []).map((department) => ({
            id: department.id ?? uniqueId('new_'),
            type: department.type,
            name: department.name,
            description: department.description,
            revenue_stream_type: department.revenue_stream_type,
            categories: department.categories.map((category) => ({
                id: category.id ?? uniqueId('new_'),
                name: category.name,
                description: category.description ?? '',
            })),
            newCategoryName: '',
            expanded: false,
        }));

        const sheets = response.sheets.map((sheet, index) => {
            const result = response.data[index] ?? 'No validation result was returned for this sheet.';
            const isValid = result === 'success';

            return {
                ...sheet,
                isValid,
                validationMessage: isValid ? '' : result.replaceAll('\\n', '\n').trim(),
            };
        });

        templateValidation.value = {
            isValid: sheets.length === 3 && sheets.every((sheet) => sheet.isValid),
            sheets,
        };
    } catch (error: unknown) {
        if (requestId !== templateValidationRequestId) {
            return;
        }

        if (axios.isAxiosError(error)) {
            const responseData = error.response?.data as
                | {
                      errors?: Record<string, string[]>;
                      message?: string;
                  }
                | undefined;
            templateValidationError.value =
                responseData?.errors?.file?.join(' ') ??
                responseData?.message ??
                'An error occurred while validating this template.';
        } else {
            templateValidationError.value = 'An error occurred while validating this template.';
        }
    } finally {
        if (requestId === templateValidationRequestId) {
            templateValidationLoading.value = false;
        }
    }
};

const getSavedId = (id: number | string | undefined): number | null => {
    if (id === undefined || !/^\d+$/.test(String(id))) {
        return null;
    }

    const parsedId = Number(id);
    return Number.isSafeInteger(parsedId) && parsedId > 0 ? parsedId : null;
};

const getEventSetups = (): EventSetupDetails[] => [
    {
        name: form.value.name.trim(),
        project_start: form.value.project_start,
        event_dates: form.value.event_dates,
    },
    ...(form.value.is_recurring
        ? form.value.recurring_options.map((option) => ({
              name: option.name.trim(),
              project_start: option.project_start,
              event_dates: option.event_dates,
          }))
        : []),
];

const captureEventSetupEvents = (eventId: number, eventName: string): void => {
    const captureWithEventContext = (posthogEventName: string): void => {
        capturePosthogEvent(posthogEventName, {
            event_id: eventId,
            event_name: eventName,
        });
    };

    form.value.departments.forEach((department) => {
        const isRevenueStream = department.type === 2;
        const isImported = getSavedId(department.id) !== null;
        let departmentEventName: string;
        if (isRevenueStream) {
            departmentEventName = isImported ? 'revenue_stream_imported' : 'revenue_stream_created';
        } else {
            departmentEventName = isImported ? 'cost_department_imported' : 'cost_department_created';
        }

        captureWithEventContext(departmentEventName);

        department.categories
            .filter((category) => getSavedId(category.id) === null)
            .forEach(() => {
                captureWithEventContext(isRevenueStream ? 'revenue_subcategory_created' : 'cost_subcategory_created');
            });
    });
};

const buildEventFormData = (eventSetup: EventSetupDetails): FormData => {
    const formData = new FormData();
    formData.append('name', eventSetup.name.trim());
    formData.append('currency_code', form.value.currency_code);
    formData.append('event_dates', eventSetup.event_dates ?? '');
    formData.append('project_start', eventSetup.project_start ?? '');
    formData.append('event_type', 'single');
    formData.append('primary_address_id', String(form.value.primary_address_id ?? ''));

    form.value.users.forEach((userId) => formData.append('users[]', String(userId)));
    form.value.tags.forEach((tagId) => formData.append('tags[]', String(tagId)));

    if (isCopyingPreviousEvent.value && form.value.cloned_event !== null) {
        formData.append('cloned_event', String(form.value.cloned_event));
    }

    const departments = form.value.departments.map((department) => {
        const departmentId = getSavedId(department.id);
        const departmentData: Record<string, unknown> = {
            ...(departmentId ? { id: departmentId } : {}),
            type: Number(department.type),
            name: department.name,
            description: department.description ?? '',
            categories: department.categories
                .filter((category) => category.name || getSavedId(category.id))
                .map((category) => {
                    const categoryId = getSavedId(category.id);
                    return {
                        ...(categoryId ? { id: categoryId } : {}),
                        ...(category.name ? { name: category.name } : {}),
                        description: category.description ?? '',
                        copy_tickets: Boolean(isCopyingPreviousEvent.value && category.copy_tickets),
                        copy_budget: Boolean(isCopyingPreviousEvent.value && category.copy_budget),
                        copy_revenue: Boolean(isCopyingPreviousEvent.value && category.copy_revenue),
                        copy_pphe: Boolean(isCopyingPreviousEvent.value && category.copy_pphe),
                        ...(category.copy_budget_option ? { copy_budget_option: category.copy_budget_option } : {}),
                        ...(category.copy_revenue_option ? { copy_revenue_option: category.copy_revenue_option } : {}),
                        ...(category.xero_account_id ? { xero_account_id: category.xero_account_id } : {}),
                    };
                }),
        };

        if (Number(department.type) === 2) {
            departmentData.stream_type = department.revenue_stream_type ?? department.stream_type ?? 3;
        }

        return departmentData;
    });
    formData.append('departments', JSON.stringify(departments));

    return formData;
};

const importTemplate = async (eventId: number, eventName: string): Promise<void> => {
    if (!templateFile.value) {
        throw new Error('Select and validate a template file before importing it.');
    }

    const formData = new FormData();
    formData.append('file', templateFile.value);
    formData.append('event_id', String(eventId));
    formData.append('skip_department_import', '1');

    await axios.post(
        route('api.onboarding.import.template', {
            account: accountId,
        }),
        formData
    );
    capturePosthogEvent('event_template_imported', {
        event_id: eventId,
        event_name: eventName,
    });
};

const createEvent = async (): Promise<void> => {
    if (isLoading.value) {
        return;
    }

    if (!isValid.value) {
        capturePosthogEvent('event_creation_failed');
        return;
    }

    isLoading.value = true;
    eventCreationError.value = '';
    const eventSetups = getEventSetups();

    try {
        while (createdEventIds.value.length < eventSetups.length) {
            const eventSetup = eventSetups[createdEventIds.value.length];
            const { data } = await axios.post<{ event_id: number }>(
                route('api.onboarding.event', { account: accountId }),
                buildEventFormData(eventSetup)
            );
            const eventId = Number(data.event_id);

            if (!Number.isSafeInteger(eventId) || eventId < 1) {
                throw new Error('The event could not be created. Please try again.');
            }

            createdEventIds.value.push(eventId);
            capturePosthogEvent('event_created', {
                event_id: eventId,
                event_name: eventSetup.name,
            });
            captureEventSetupEvents(eventId, eventSetup.name);
        }

        if (createMethod.value === 'template') {
            for (const [index, eventId] of createdEventIds.value.entries()) {
                if (importedTemplateEventIds.value.includes(eventId)) {
                    continue;
                }

                const eventName = eventSetups[index]?.name ?? form.value.name;
                await importTemplate(eventId, eventName);
                importedTemplateEventIds.value.push(eventId);
            }
        }

        const mainEventId = createdEventIds.value[0];
        if (mainEventId === undefined) {
            throw new Error('The event could not be created. Please try again.');
        }

        window.location.href = route('account.event.dashboard', {
            account: accountId,
            event: mainEventId,
        });
    } catch (error: unknown) {
        const responseData = axios.isAxiosError(error)
            ? (error.response?.data as { errors?: Record<string, string | string[]>; message?: string } | undefined)
            : undefined;
        const validationMessages = Object.values(responseData?.errors ?? {}).flat();
        const detail = validationMessages.join(' ') || responseData?.message;
        const hasUncreatedEvents = createdEventIds.value.length < eventSetups.length;
        const hasUnimportedEvents =
            createMethod.value === 'template' && importedTemplateEventIds.value.length < createdEventIds.value.length;

        if (hasUncreatedEvents) {
            capturePosthogEvent('event_creation_failed');
            eventCreationError.value =
                detail ??
                (createdEventIds.value.length
                    ? `${createdEventIds.value.length} of ${eventSetups.length} events were created. Retry to finish creating the series.`
                    : 'There was an error creating your event. Please check the details and try again.');
        } else if (hasUnimportedEvents) {
            eventCreationError.value =
                detail ??
                'The events were created, but some template data could not be imported. Retry to finish setup.';
        } else {
            eventCreationError.value =
                detail ?? 'There was an error creating your event. Please check the details and try again.';
        }
    } finally {
        isLoading.value = false;
    }
};

const creatingRevenueStream = ref<string>('');
const addingExistingRevenueStream = ref<boolean>(false);
const addingExistingSubcategoryForDepartment = ref<string | null>(null);
const selectedExistingSubcategories = ref<Record<string, ExistingSubcategoryOption | null>>({});
const creatingDepartment = ref<string>('');
const addingExistingDepartment = ref<boolean>(false);
const existingEventSearch = ref<string>('');
const accountEvents = ref<EventListItem[]>([]);
const recentlyEndedEvents = ref<EventListItem[]>([]);

const openEventWizard = (): void => {
    modal.value?.open();
    getAccountEvents();
    getRecentlyEndedEvents();
};

onMounted(() => {
    Eventbus.$on('create-event:open', openEventWizard);
});

onUnmounted(() => {
    Eventbus.$off('create-event:open', openEventWizard);
});

const isCopyingPreviousEvent = computed(() => createMethod.value === 'copy' && form.value.cloned_event !== null);

const availableSteps = computed(() => {
    switch (createMethod.value) {
        case 'quick':
            return ['Choose Method', 'Details', 'Departments', 'Review'];
        case 'template':
            return ['Choose Method', 'Details', 'Select Template', 'Departments', 'Review'];
        case 'copy':
            return ['Choose Method', 'Select Event', 'Details', 'Departments', 'Review'];
        case 'ai':
            return ['Choose Method', 'AI Setup', 'Generation', 'Review'];
        default:
            return ['Choose Method', 'Details', 'Departments', 'Review'];
    }
});

const recurringOptionsAreValid = computed(() => {
    if (!form.value.is_recurring) {
        return true;
    }

    if (
        form.value.recurring_options.some(
            (option) => !option.name.trim() || !option.event_dates || !option.project_start
        )
    ) {
        return false;
    }

    const names = [form.value.name, ...form.value.recurring_options.map((option) => option.name)].map((name) =>
        name.trim().toLowerCase()
    );

    return new Set(names).size === names.length;
});

const seriesEventCount = computed(() => 1 + (form.value.is_recurring ? form.value.recurring_options.length : 0));
const createButtonLabel = computed(() => {
    if (createdEventIds.value.length > 0 && createdEventIds.value.length < seriesEventCount.value) {
        return 'Retry Event Series Setup';
    }
    if (createdEventIds.value.length > 0 && createMethod.value === 'template') {
        return 'Retry Template Import';
    }
    return form.value.is_recurring && form.value.recurring_options.length > 0 ? 'Create Event Series' : 'Create Event';
});

const isValid = computed(() => {
    if (!form.value.name.trim() || !form.value.event_dates || !form.value.project_start || !form.value.currency_code) {
        return false;
    }
    if (!isAddressValid.value || form.value.departments.some((department) => !department.categories.length)) {
        return false;
    }
    if (!recurringOptionsAreValid.value) {
        return false;
    }
    if (createMethod.value === 'template' && (!templateFile.value || !templateValidation.value?.isValid)) {
        return false;
    }
    return true;
});

const getDefaultProjectStart = (eventDates: string | null): string | null => {
    if (!eventDates) {
        return null;
    }

    const eventEndDate = eventDates.split(' to ').pop()?.trim();
    const dateParts = eventEndDate?.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (!dateParts) {
        return null;
    }

    const year = Number(dateParts[1]);
    const month = Number(dateParts[2]);
    const day = Number(dateParts[3]);
    const date = new Date(year, month - 1, day);

    if (date.getFullYear() !== year || date.getMonth() !== month - 1 || date.getDate() !== day) {
        return null;
    }

    date.setDate(date.getDate() + 1);

    return [
        date.getFullYear(),
        String(date.getMonth() + 1).padStart(2, '0'),
        String(date.getDate()).padStart(2, '0'),
    ].join('-');
};

const setMainEventDates = (eventDates: string | null): void => {
    const nextDefault = getDefaultProjectStart(eventDates);

    if (!form.value.project_start || form.value.project_start === mainProjectStartDefault.value) {
        form.value.project_start = nextDefault ?? '';
    }

    mainProjectStartDefault.value = nextDefault;
    form.value.event_dates = eventDates ?? '';
};

const setRecurringEventDates = (index: number, eventDates: string | null): void => {
    const option = form.value.recurring_options[index];
    if (!option) {
        return;
    }

    const nextDefault = getDefaultProjectStart(eventDates);
    if (!option.project_start || option.project_start === option.project_start_default) {
        option.project_start = nextDefault;
    }

    option.project_start_default = nextDefault;
    option.event_dates = eventDates;
};

const resizeRecurringOptions = (value: string | number | null): void => {
    const parsedCount = Number(value);
    const count = Number.isFinite(parsedCount) ? Math.max(0, Math.min(9, Math.trunc(parsedCount))) : 0;
    const options = form.value.recurring_options;

    if (count < options.length) {
        options.splice(count);
        return;
    }

    while (options.length < count) {
        const eventDates = form.value.event_dates || null;
        const defaultProjectStart = getDefaultProjectStart(eventDates);

        options.push({
            name: `${form.value.name || 'Event'} ${options.length + 2}`,
            project_start: defaultProjectStart ?? form.value.project_start ?? null,
            project_start_default: defaultProjectStart,
            event_dates: eventDates,
        });
    }
};

const handleVenueSelection = (address: Record<string, unknown> | null): void => {
    if (!address) {
        form.value.primary_address = null;
        return;
    }

    const rawId = address.id;
    if ((typeof rawId !== 'string' && typeof rawId !== 'number') || rawId === '') {
        form.value.primary_address = null;
        return;
    }

    const id = Number(rawId);
    if (!Number.isInteger(id)) {
        form.value.primary_address = null;
        return;
    }

    form.value.primary_address = {
        id,
        name: typeof address.name === 'string' ? address.name : '',
        address1: typeof address.address1 === 'string' ? address.address1 : '',
        address2: typeof address.address2 === 'string' ? address.address2 : '',
        city: typeof address.city === 'string' ? address.city : '',
        postcode: typeof address.postcode === 'string' ? address.postcode : '',
        country: typeof address.country === 'string' ? address.country : '',
        address: typeof address.address === 'string' ? address.address : '',
    };
};

const openCreateVenueModal = (name: string): void => {
    venueAutocomplete.value?.close();
    createVenueModal.value?.open(name);
};

const handleVenueCreated = (address: VenueOption): void => {
    venueAutocomplete.value?.selectOption(address);
};

const handleAddDepartment = (text: string, attributes: DepartmentCreationAttributes): void => {
    form.value.departments.push({
        id: attributes.id ?? uniqueId('new_'),
        type: attributes.type,
        name: text,
        description: attributes.description ?? '',
        revenue_stream_type: attributes.revenue_stream_type,
        stream_type: attributes.stream_type,
        categories: attributes.categories ?? [],
        newCategoryName: '',
        expanded: false,
    });
    creatingRevenueStream.value = '';
    creatingDepartment.value = '';
};
const handleAddExistingRevenueStream = () => {
    if (!existingRevenueStreamSelector.value) {
        return;
    }
    const selectedOptions = existingRevenueStreamSelector.value.selectedOptions as ExistingDepartmentOption[];
    if (!selectedOptions || selectedOptions.length === 0) {
        return;
    }
    handleAddDepartment(selectedOptions[0].name, {
        id: selectedOptions[0].id,
        type: selectedOptions[0].type,
        description: selectedOptions[0].description ?? '',
        revenue_stream_type: selectedOptions[0].revenue_stream_type,
        categories:
            selectedOptions[0].categories.map((category) => ({
                id: category.id,
                name: category.name,
                description: category.description ?? '',
            })) ?? [],
    });
    addingExistingRevenueStream.value = false;
};
const handleAddExistingDepartment = () => {
    if (!existingDepartmentSelector.value) {
        return;
    }
    const selectedOptions = existingDepartmentSelector.value.selectedOptions as ExistingDepartmentOption[];
    if (!selectedOptions || selectedOptions.length === 0) {
        return;
    }
    handleAddDepartment(selectedOptions[0].name, {
        id: selectedOptions[0].id,
        type: selectedOptions[0].type,
        description: selectedOptions[0].description ?? '',
        categories:
            selectedOptions[0].categories.map((category) => ({
                id: category.id,
                name: category.name,
                description: category.description ?? '',
            })) ?? [],
    });
    addingExistingDepartment.value = false;
};

const handleAddCategory = (departmentId: string | number, categoryName: string) => {
    const department = form.value.departments.find((d) => String(d.id) === String(departmentId));
    if (department) {
        department.categories.push({
            id: uniqueId('new_'),
            name: categoryName,
        });
        department.newCategoryName = '';
    }
};

const getExistingSubcategoryExclusions = (categories: Array<{ id: number | string }>): string[] => {
    const categoryIds = categories.map((category) => category.id).filter((id) => !Number.isNaN(Number(id)));

    return categoryIds.length ? [`categories.id=${categoryIds.join(',')}`] : [];
};

const startAddingExistingSubcategory = (departmentId: string | number): void => {
    const departmentKey = String(departmentId);
    selectedExistingSubcategories.value[departmentKey] = null;
    addingExistingSubcategoryForDepartment.value = departmentKey;
};

const setSelectedExistingSubcategory = (departmentId: string | number, option: unknown): void => {
    const departmentKey = String(departmentId);
    if (typeof option !== 'object' || option === null) {
        selectedExistingSubcategories.value[departmentKey] = null;
        return;
    }

    const selectedOption = option as Record<string, unknown>;
    if (
        (typeof selectedOption.id !== 'number' && typeof selectedOption.id !== 'string') ||
        typeof selectedOption.name !== 'string'
    ) {
        selectedExistingSubcategories.value[departmentKey] = null;
        return;
    }

    selectedExistingSubcategories.value[departmentKey] = {
        id: selectedOption.id,
        name: selectedOption.name,
        description: typeof selectedOption.description === 'string' ? selectedOption.description : '',
    };
};

const cancelAddingExistingSubcategory = (): void => {
    if (addingExistingSubcategoryForDepartment.value !== null) {
        selectedExistingSubcategories.value[addingExistingSubcategoryForDepartment.value] = null;
    }

    addingExistingSubcategoryForDepartment.value = null;
};

const handleAddExistingSubcategory = (department: Pick<EventWizardDepartment, 'id' | 'categories'>): void => {
    const departmentKey = String(department.id);
    const selectedSubcategory = selectedExistingSubcategories.value[departmentKey];
    if (!selectedSubcategory) {
        return;
    }

    const alreadyAdded = department.categories.some(
        (category) => String(category.id) === String(selectedSubcategory.id)
    );
    if (!alreadyAdded) {
        department.categories.push(selectedSubcategory);
    }

    selectedExistingSubcategories.value[departmentKey] = null;
    addingExistingSubcategoryForDepartment.value = null;
};

const flatpickrRangeAllowSingle = (selectedDates: Date[], _dateStr: string, instance: flatpickr.Instance): void => {
    if (!selectedDates.length) {
        return;
    }

    instance.setDate([selectedDates[0], selectedDates[+(selectedDates.length > 1)]], true);
};

const prefillFromPreviousEvent = async (event: { id: number }): Promise<void> => {
    if (isLoading.value) {
        return;
    }

    isLoading.value = true;
    eventPrefillError.value = '';

    try {
        const { data: sourceEvent } = await axios.get<EventCloneDetails>(
            route('api.onboarding.events.single', {
                account: accountId,
                event: event.id,
            })
        );

        form.value.cloned_event = sourceEvent.id;
        form.value.name = sourceEvent.name;
        form.value.primary_address_id = sourceEvent.primary_address?.id ?? sourceEvent.primary_address_id ?? null;
        form.value.primary_address = sourceEvent.primary_address;
        form.value.event_dates = [sourceEvent.starts_at, sourceEvent.ends_at]
            .map((date) => String(date ?? '').slice(0, 10))
            .filter(Boolean)
            .join(' to ');
        mainProjectStartDefault.value = getDefaultProjectStart(form.value.event_dates);
        form.value.project_start = String(sourceEvent.project_start ?? '').slice(0, 10);
        form.value.currency_code = sourceEvent.currency_code;

        const accountUserIds = new Set(
            ((useDataStore().get('account.users') ?? []) as Array<{ id: number | string }>).map((user) =>
                String(user.id)
            )
        );
        form.value.users = sourceEvent.users
            .filter((user) => accountUserIds.has(String(user.id)))
            .map((user) => Number(user.id));
        form.value.tags = sourceEvent.assigned_tags.map((tag) => Number(tag.id));

        const mapCategories = (categories: EventCloneCategory[]) =>
            categories.map((category) => ({
                id: category.id,
                name: category.name,
                description: category.description ?? '',
                copy_tickets: false,
                copy_budget: false,
                copy_revenue: false,
                copy_pphe: false,
                copy_budget_option: null,
                copy_revenue_option: null,
            }));

        form.value.departments = [
            ...sourceEvent.revenue_departments.map((department) => ({
                id: department.id,
                type: 2 as const,
                name: department.name,
                description: department.description ?? '',
                revenue_stream_type:
                    department.revenue_stream_type === null || department.revenue_stream_type === undefined
                        ? 3
                        : Number(department.revenue_stream_type),
                categories: mapCategories(department.categories),
                newCategoryName: '',
                expanded: false,
            })),
            ...sourceEvent.cost_departments.map((department) => ({
                id: department.id,
                type: 1 as const,
                name: department.name,
                description: department.description ?? '',
                categories: mapCategories(department.categories),
                newCategoryName: '',
                expanded: false,
            })),
        ];

        currentStep.value = 'Details';
    } catch (error: unknown) {
        const responseData = axios.isAxiosError(error)
            ? (error.response?.data as { message?: string } | undefined)
            : undefined;
        eventPrefillError.value = responseData?.message ?? 'Unable to load the selected event. Please try again.';
    } finally {
        isLoading.value = false;
    }
};

const getAccountEvents = debounce(async () => {
    const { data } = await axios.get<{ data: EventListItem[] }>(
        route('api.account.events', {
            account: accountId,
            sort: 'id=desc',
            search: existingEventSearch.value,
            paginated: 1,
        })
    );
    accountEvents.value = data.data;
}, 300); // Debounce with a 300ms delay

const getRecentlyEndedEvents = async () => {
    const { data } = await axios.get<{ data: EventListItem[] }>(
        route('api.account.events', {
            account: accountId,
            date_to: new Date().toISOString(),
            sort: 'ends_at=desc',
            paginated: 1,
        })
    );
    recentlyEndedEvents.value = data.data;
};
</script>
